import {
  Box,
  Flex,
  Text,
  useTheme,
  VStack,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { getRole, UserRole } from '@devd-client/api';
import { useState, MouseEvent } from 'react';
import { MdAdd, MdDeleteOutline, MdEdit } from 'react-icons/md';

export interface Chat {
  id: string;
  title: string;
  lastMessage?: string;
  updatedAt: string;
}

export interface ChatSidebarProps {
  chats: Chat[];
  selectedChatId?: string;
  onChatSelect: (chatId: string) => void;
  onNewChat: () => void;
  onDeleteChat?: (chatId: string) => void;
  onEditChat?: (chatId: string) => void;
  isLoading?: boolean;
  deletingChatId?: string | null;
  isCreatingNewChat: boolean;
}

export const ChatSidebar = ({
  chats,
  selectedChatId,
  onChatSelect,
  onNewChat,
  onDeleteChat,
  onEditChat,
  isLoading,
  deletingChatId,
  isCreatingNewChat,
}: ChatSidebarProps) => {
  const { colors } = useTheme();
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);

  return (
    <Box
      width="280px"
      minWidth="280px"
      height="100%"
      borderRight="1px solid #E0E0E0"
      bg="#FBFBFB"
      display="flex"
      flexDirection="column"
      position="relative"
    >
      <Box p={2.5} borderBottom="1px solid #E0E0E0" bg="white">
        <Flex
          as="button"
          onClick={onNewChat}
          width="100%"
          align="center"
          gap={2.5}
          px={3}
          py={2.5}
          borderRadius="8px"
          bg="transparent"
          color={
            getRole() === UserRole.EMPLOYEE ? 'text.secondary' : 'text.primary'
          }
          fontSize="sm"
          fontWeight="medium"
          _hover={{
            bg: '#F7F7F8',
          }}
          _active={{
            bg: '#E3E6EA',
          }}
          transition="background 0.15s ease"
          cursor={
            getRole() === UserRole.EMPLOYEE || isCreatingNewChat
              ? 'not-allowed'
              : 'pointer'
          }
          border="none"
          disabled={getRole() === UserRole.EMPLOYEE || isCreatingNewChat}
        >
          {isCreatingNewChat ? (
            <Spinner size="xs" color="gray.400" mr={2} />
          ) : (
            <MdAdd size={20} color={colors.primary} />
          )}
          <Text>New Chat</Text>
        </Flex>
      </Box>

      <VStack flex={1} overflowY="auto" align="stretch" spacing={0} p={2}>
        {isLoading ? (
          <Center py={8}>
            <Spinner size="sm" color="gray.400" />
          </Center>
        ) : chats.length === 0 ? (
          <Box p={6} textAlign="center">
            <Text fontSize="sm" color="text.secondary">
              No conversations yet
            </Text>
          </Box>
        ) : (
          chats.map((chat) => {
            const isSelected = chat.id === selectedChatId;
            const isHovered = hoveredChatId === chat.id;

            return (
              <Flex
                key={chat.id}
                onMouseEnter={() => setHoveredChatId(chat.id)}
                onMouseLeave={() => setHoveredChatId(null)}
                px={4}
                py={3}
                mx={1.5}
                borderRadius="8px"
                cursor="pointer"
                align="center"
                justify="space-between"
                gap={2}
                position="relative"
                bg={isSelected ? 'rgba(102, 95, 241, 0.1)' : 'transparent'}
                _hover={{
                  bg: isSelected
                    ? 'rgba(102, 95, 241, 0.12)'
                    : 'rgba(0, 0, 0, 0.02)',
                }}
                onClick={() => onChatSelect(chat.id)}
                transition="all 0.2s ease"
              >
                <Text
                  fontSize="sm"
                  fontWeight={isSelected ? 'semibold' : 'normal'}
                  color={isSelected ? colors.primary : 'text.primary'}
                  noOfLines={1}
                  flex={1}
                  minWidth={0}
                  title={chat.title}
                >
                  {chat.title}
                </Text>
                {(onEditChat || onDeleteChat) && (
                  <Flex
                    align="center"
                    opacity={isHovered ? 1 : 0}
                    transition="opacity 0.15s ease"
                    flexShrink={0}
                    minW={onEditChat && onDeleteChat ? '44px' : '22px'}
                  >
                    {onEditChat && (
                      <Box
                        as="button"
                        onClick={(e: MouseEvent) => {
                          e.stopPropagation();
                          onEditChat(chat.id);
                        }}
                        p={1.5}
                        borderRadius="4px"
                        color="gray.500"
                        pointerEvents={isHovered ? 'auto' : 'none'}
                        _hover={{
                          bg: 'rgba(102, 95, 241, 0.08)',
                          color: colors.primary,
                        }}
                        transition="all 0.15s ease"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        minW="24px"
                        h="24px"
                      >
                        <MdEdit size={14} />
                      </Box>
                    )}
                    {onDeleteChat && (
                      <Box
                        as="button"
                        onClick={(e: MouseEvent) => {
                          e.stopPropagation();
                          if (deletingChatId !== chat.id) {
                            onDeleteChat(chat.id);
                          }
                        }}
                        p={1.5}
                        borderRadius="4px"
                        color="gray.500"
                        pointerEvents={
                          deletingChatId === chat.id ||
                          (isHovered && deletingChatId !== chat.id)
                            ? 'auto'
                            : 'none'
                        }
                        opacity={
                          deletingChatId === chat.id ? 1 : isHovered ? 1 : 0
                        }
                        cursor={
                          deletingChatId === chat.id ? 'not-allowed' : 'pointer'
                        }
                        _hover={{
                          bg: 'rgba(239, 68, 68, 0.08)',
                          color: 'red.500',
                        }}
                        transition="all 0.15s ease"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        minW="24px"
                        h="24px"
                        disabled={deletingChatId === chat.id}
                      >
                        {deletingChatId === chat.id ? (
                          <Spinner size="xs" color="gray.500" />
                        ) : (
                          <MdDeleteOutline size={14} />
                        )}
                      </Box>
                    )}
                  </Flex>
                )}
              </Flex>
            );
          })
        )}
      </VStack>
    </Box>
  );
};

export default ChatSidebar;
